import React, { useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import { uniq } from "lodash";
import Dialog from "components/Dialog";
import { DIALOG_ACTION_TITLE, INPUT } from "shared/labels";
import HtmlText from "components/HtmlText";
import { handlePlotHover } from "../GraphElementGraph/utils";
import GraphElementGraph from "../GraphElementGraph";
import { parameterTypesSelector } from "store/global/global.selectors";
import { ResultElementParameterType, ResultElementSourceType, ResultSetElementType, } from "store/rs-workspace/rs-workspace.types";
import { workspaceNotRefinedCellModelsSelector, workspaceRecipesSelector, } from "store/rs-workspace/selectors/rs-workspace.selectors";
import { getCombinationParameterPreview } from "store/rs-workspace/utils/common.utils";
import { getDetailedDurationString, getSecondsFromConvertedTime } from "utils/dateTime";
import { getNumberFromNumberString, renderNumberValue } from "utils/string";
import * as styles from "./styles.module.scss";
const GraphElementErrorsGraph = ({ isOpen, onClose, elementData, graphData, preparedSource, plotLayout, timeUnit, error, isDiscreteGraph, discreteXTicksMapping, }) => {
    const parameterTypes = useSelector(parameterTypesSelector);
    const recipes = useSelector(workspaceRecipesSelector);
    const cellModels = useSelector(workspaceNotRefinedCellModelsSelector);
    const isLineGraph = useMemo(() => elementData.type === ResultSetElementType.LineGraph, [elementData.type]);
    const parameter = useMemo(() => elementData.parameter, [elementData]);
    const title = useMemo(() => {
        return `${elementData.name || (isLineGraph ? "Line Graph" : "Process Data Viewer")} - ${preparedSource?.source?._sourceName || INPUT.UNDEFINED} - Errors overview`;
    }, [elementData.name, isLineGraph, preparedSource?.source?._sourceName]);
    const preparedParameter = useMemo(() => (isLineGraph ? getCombinationParameterPreview(parameter, recipes, cellModels, parameterTypes) : {}), [cellModels, parameter, parameterTypes, recipes, isLineGraph]);
    const parameterName = useMemo(() => preparedParameter?.parameter?.name, [preparedParameter?.parameter?.name]);
    const parameterUnit = useMemo(() => preparedParameter.parameter?.type === ResultElementParameterType.ALTERNATIVE_FLOW
        ? ""
        : preparedParameter?.parameter?.units, [preparedParameter.parameter]);
    const renderHeader = useCallback(() => {
        const variable = preparedSource?.source?._variableName?.label;
        const units = preparedSource?.source?.units?.label;
        const title = preparedSource?.source?.type === ResultElementSourceType.Insight
            ? preparedSource?.source?._sourceName
            : `${variable} ${units ? `(${units})` : ""}`;
        return (React.createElement("div", { className: styles.legend },
            React.createElement(HtmlText, { text: title, targetTagName: "p" }),
            React.createElement("p", null, "Error %")));
    }, [preparedSource]);
    const getRegularGraphTitleInnerHtml = useCallback((legendTitleInnerHtml) => {
        const xValue = renderNumberValue(getNumberFromNumberString(legendTitleInnerHtml));
        const unit = parameterUnit ? `(${parameterUnit.replaceAll("span", "tspan")})` : "";
        return `${parameterName}: ${xValue} ${unit}`;
    }, [parameterName, parameterUnit]);
    const getDiscreteGraphTitleInnerHtml = useCallback((ev) => {
        const eventXPoints = uniq(ev?.points?.map(({ x }) => Math.floor(Number(x))));
        if (eventXPoints.length === 1 && parameterName) {
            const flowName = discreteXTicksMapping?.find((_, idx) => idx === eventXPoints[0])?.name;
            return `${parameterName} ${flowName ? `: ${flowName}` : ""}`;
        }
        return parameterName ?? "";
    }, [discreteXTicksMapping, parameterName]);
    const onLineGraphPlotHover = useCallback((plotRef) => (ev) => handlePlotHover(plotRef, (legendTitleInnerHtml) => !isDiscreteGraph
        ? getRegularGraphTitleInnerHtml(legendTitleInnerHtml)
        : getDiscreteGraphTitleInnerHtml(ev), ev), [getDiscreteGraphTitleInnerHtml, getRegularGraphTitleInnerHtml, isDiscreteGraph]);
    const onProcessDataPlotHover = useCallback((plotRef) => (ev) => handlePlotHover(plotRef, (legendTitleInnerHtml) => getDetailedDurationString(getSecondsFromConvertedTime(getNumberFromNumberString(legendTitleInnerHtml), timeUnit)), ev), [timeUnit]);
    return (React.createElement(Dialog, { className: styles.dialog, title: title, isOpen: isOpen, contentClassName: styles.content, primaryButton: { label: DIALOG_ACTION_TITLE.CLOSE, onClick: onClose } },
        React.createElement("div", { className: styles.root },
            React.createElement("div", { className: styles.graph },
                React.createElement(GraphElementGraph, { className: styles.plot, plotLayout: plotLayout, graphData: graphData, onPlotHover: isLineGraph ? onLineGraphPlotHover : onProcessDataPlotHover, renderHeader: renderHeader })),
            React.createElement("div", { className: styles.errors },
                React.createElement("h5", null, "Errors:"),
                React.createElement("p", null, error)))));
};
export default GraphElementErrorsGraph;
